import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, share, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Declare interface for Google Analytics 4
declare global {
    interface Window {
        dataLayer: any;
    }
}
interface DecodedCsrfToken {
    identityType: string;
    'x-uuid': string;
}

export function replaceDomainUrl(url: string) {
    const base = window.location.hostname.split('.')[1];
    return base === 'viessmann' ? url.replace('viessmann-climatesolutions.com', 'viessmann.com') : url;
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private httpClient = inject(HttpClient);

    public csrfToken$ = new BehaviorSubject<string | undefined>(undefined);
    private fetchNewCsrfToken$: Observable<{ token: string }> = new Observable(undefined);

    public getCsrfToken(): string | undefined {
        return this.csrfToken$.value;
    }

    public getCsrfToken$() {
        return this.csrfToken$.pipe(
            filter((csrfToken) => csrfToken !== undefined && this.isCsrfTokenValid(csrfToken)),
            map((csrfToken) => {
                const decodedCsrfToken = jwtDecode<DecodedCsrfToken>(csrfToken!);
                const identityType = decodedCsrfToken.identityType;
                const userId = decodedCsrfToken['x-uuid'];

                // Push data to window.dataLayer
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'user_profile_loaded',
                    identityType: identityType,
                    userId: userId,
                    userProfileLoaded: true,
                });
                return csrfToken;
            }),
        );
    }

    init() {
        this.fetchNewCsrfToken$ = this.getCsrfTokenRequest().pipe(
            tap({
                next: ({ token }) => {
                    return this.csrfToken$.next(token);
                },
                error: () => {
                    return this.redirectToLoginScreen();
                },
            }),
            share(),
        );

        const token = this.getCsrfTokenFromUrl();

        if (token && this.isCsrfTokenValid(token)) {
            this.csrfToken$.next(token);
        } else {
            this.fetchNewCsrfToken$.subscribe();
        }
    }

    onLogout() {
        return;
    }

    fetchNewCsrfToken() {
        return this.fetchNewCsrfToken$;
    }

    public getCsrfTokenRequest() {
        return this.httpClient.post<{ token: string }>(
            `${replaceDomainUrl(environment.auth.baseUrl)}/auth/v1/saml/csrf`,
            {
                appId: environment.auth.appId,
            },
            {
                withCredentials: true,
            },
        );
    }

    private getCsrfTokenFromUrl(): string | void {
        const hash = window.location.hash;
        if (hash?.startsWith('#token=')) {
            const value = new HttpParams({ fromString: hash }).get('#token');
            window.location.hash = ''; // remove token form url
            return value || undefined;
        }
    }

    private redirectToLoginScreen() {
        window.location.href = `${environment.auth.baseUrl}/saml/sso/request?appId=${
            environment.auth.appId
        }&redirectUrl=${encodeURIComponent(window.location.href)}`;
    }

    private isCsrfTokenValid(token: string) {
        try {
            return !this.isCSRFTokenExpired(token);
        } catch (_) {
            return false;
        }
    }

    isCSRFTokenExpired(token: string): boolean {
        const date = this.getTokenExpirationDate(token);
        if (!date) {
            return false;
        }
        return date.valueOf() <= new Date().valueOf();
    }

    getTokenExpirationDate(token: string): Date | null {
        const decoded = <{ exp?: number }>jwtDecode(token);

        if (decoded.exp === undefined) {
            return null;
        }

        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);
        return date;
    }
}
